import {
  KeyboardLayouts,
  Key,
  IconKey,
  LayoutKey,
  DEFAULT_KEY_WIDTH,
} from 'components/common/input/Keys';

export const DEFAULT_KEYBOARD_LAYOUT = KeyboardLayouts.LOWERCASE_ABC;
const DEFAULT_SPACING = 10;

const getKeyWidth = (size = 1) => {
  return DEFAULT_KEY_WIDTH * size + DEFAULT_SPACING * (size - 1);
};

const BACKSPACE_KEY_SIZING = {
  width: 29,
  height: 22,
};

const ARROW_KEY_SIZING = {
  width: 9,
  height: 14,
};

const ENTER_KEY_SIZING = {
  width: 26,
  height: 26,
};

const BASE_KEYBOARD = {
  styling: {
    horizontalSpacing: DEFAULT_SPACING,
    verticalSpacing: DEFAULT_SPACING,
  },
  buttonTypes: {
    default: {
      type: Key,
    },
    Backspace: {
      type: IconKey,
      icon: 'static/images/keyboard/keyboard-backspace.svg',
      iconSizing: BACKSPACE_KEY_SIZING,
    },
    Layout: {
      type: LayoutKey,
    },
    Left: {
      type: IconKey,
      icon: 'static/images/keyboard/keyboard-left.svg',
      iconSizing: ARROW_KEY_SIZING,
    },
    Right: {
      type: IconKey,
      icon: 'static/images/keyboard/keyboard-right.svg',
      iconSizing: ARROW_KEY_SIZING,
    },
    InputComplete: {
      type: Key,
      w: getKeyWidth(2),
    },
    PinComplete: {
      type: IconKey,
      icon: 'static/images/keyboard/keyboard-pin-enter.svg',
      iconSizing: ENTER_KEY_SIZING,
    },
    Space: {
      type: Key,
      w: getKeyWidth(3),
    },
    Clear: {
      type: Key,
      w: getKeyWidth(2),
    },
  },
};

const KEYBOARD_LAYOUT_KEYS = [
  KeyboardLayouts.LOWERCASE_ABC,
  KeyboardLayouts.UPPERCASE_ABC,
  KeyboardLayouts.NUMERIC,
  KeyboardLayouts.SYMBOLS,
] as const;

type LayoutOverride = {
  layouts: readonly KeyboardLayouts[];
  replace: Record<string, string>;
};

// prettier-ignore
const KEYBOARD_LAYOUTS: Record<(typeof KEYBOARD_LAYOUT_KEYS)[number], string[][]> = {
  [KeyboardLayouts.LOWERCASE_ABC]: [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ','],
    [`Layout:${KeyboardLayouts.UPPERCASE_ABC}`, 'z', 'x', 'c', 'v', 'b', 'n', 'm', '.', 'Backspace'],
    [`Layout:${KeyboardLayouts.NUMERIC}`, 'Left', 'Right', 'Space:space', '-', '_', 'InputComplete:enter'],
  ],
  [KeyboardLayouts.UPPERCASE_ABC]: [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', ','],
    [`Layout:${KeyboardLayouts.LOWERCASE_ABC}`, 'Z', 'X', 'C', 'V', 'B', 'N', 'M', '.', 'Backspace'],
    [`Layout:${KeyboardLayouts.NUMERIC}`, 'Left', 'Right', 'Space:space', '-', '_', 'InputComplete:enter'],
  ],
  [KeyboardLayouts.NUMERIC]: [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['@', '#', '$', '_', '&', '-', '+', '(', ')', '/'],
    [`Layout:${KeyboardLayouts.SYMBOLS}`, '*', '"', '\'', ':', ';', '!', '?', '%', 'Backspace'],
    [`Layout:${KeyboardLayouts.LOWERCASE_ABC}`, 'Left', 'Right', 'Space:space', '.', ',', 'InputComplete:enter'],
  ],
  [KeyboardLayouts.SYMBOLS]: [
    ['~', '`', '|', '•', '√', 'π', '÷', 'x', '¶', '∆'],
    ['£', '¢', '€', '¥', '^', '˚', '=', '{', '}', ''],
    [`Layout:${KeyboardLayouts.NUMERIC}`, '%', '©', '®', '✔', '[', ']', '<', '>', 'Backspace'],
    [`Layout:${KeyboardLayouts.LOWERCASE_ABC}`, 'Left', 'Right', 'Space:space', '.', ',', 'InputComplete:enter'],
  ],
};

const PIN_LAYOUT = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['0', 'Backspace', 'PinComplete:Enter'],
];

function generateKeyboardConfig(layoutOverrides: LayoutOverride[] = []) {
  const layouts: Partial<Record<KeyboardLayouts, string[][]>> = {};

  KEYBOARD_LAYOUT_KEYS.forEach(name => {
    const layout = KEYBOARD_LAYOUTS[name].map(row => [...row]);
    const override = layoutOverrides.find(o => o.layouts.includes(name));

    if (override) {
      for (let i = 0; i < layout.length; i++) {
        const row = layout[i];
        if (!row) continue;
        layout[i] = row.map(key => override.replace[key] ?? key);
      }
    }

    layouts[name] = layout;
  });
  return layouts;
}

export const KeyboardConfigWithClear = {
  ...BASE_KEYBOARD,
  layouts: generateKeyboardConfig([
    {
      layouts: KEYBOARD_LAYOUT_KEYS,
      replace: { 'InputComplete:enter': 'Clear:clear' },
    },
  ]),
};

export const EmailKeyboard = {
  ...BASE_KEYBOARD,
  layouts: generateKeyboardConfig([
    {
      layouts: [KeyboardLayouts.LOWERCASE_ABC, KeyboardLayouts.UPPERCASE_ABC],
      replace: { _: '@' },
    },
  ]),
};

export default {
  ...BASE_KEYBOARD,
  layouts: {
    ...generateKeyboardConfig(),
    [KeyboardLayouts.PIN]: PIN_LAYOUT,
  },
};
